import React, { useEffect, useState, useMemo, useContext } from "react";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import CommonAccordion from "../../custom-components/CommonAccordion";
import MultiAccordian from "./MultiAccordian";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { generateTestConditions } from "../../../services/webServices";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MultipleTestCasesContext,
  TestCasesLoaderContext,
} from "../../main-pages/MultiTestCaseGenerationPage";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CommonDownload from "../../custom-components/CommonDownload";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import SelectionErrorPopUp from "../../popup-components/SelectionerrorPop";
import formatDataForPDF from "../../../utils/FormatDataForPDF";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const MultipleUseCase = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { testCaseLoaderState, setTestCaseLoaderState } = useContext(
    TestCasesLoaderContext
  );
  const multipleTestCasesContext = useContext(MultipleTestCasesContext);
  const multipleUseCasesFromContext = multipleTestCasesContext.multipleUseCases;
  const selectedUseCasesFromContext = multipleTestCasesContext.selectedUseCases;
  const functionalSpecDocument =
    multipleTestCasesContext.functionalSpecDocument;
  const testConditions = multipleTestCasesContext.testConditions;

  const [openDownload, setOpenDownload] = useState(false);

  const [multipleUseCase, setMultipleUseCase] = useState(
    multipleUseCasesFromContext
  );
  const [selectedUseCase, setSelectedUseCase] = useState(
    selectedUseCasesFromContext
  );
  const [isEditing, setIsEditing] = useState(null);
  const [editingValue, setEditingValue] = useState({});
  const [noneUseCaseSelected, setNoneUseCaseSelected] = useState(false);

  const { setLoadingState } = useContext(LoadingModalContext);

  const defaultUseCaseExpanded =
    !testConditions?.length && Object.keys(multipleUseCase || {}).length > 0;

  useEffect(() => {
    setMultipleUseCase(multipleUseCasesFromContext);
    setSelectedUseCase(selectedUseCasesFromContext);
  }, [multipleUseCasesFromContext, selectedUseCasesFromContext]);

  const activeUseCases = [];
  multipleUseCase.map((eachUseCase, index) => {
    if (!!eachUseCase) {
      activeUseCases.push({ [index]: eachUseCase });
    }
  });

  const summaries = useMemo(
    () =>
      multipleUseCase.map((eachUseCase) => {
        return eachUseCase?.["Title"];
      }),
    [multipleUseCase]
  );

  const useCaseComponents = multipleUseCase.map((eachUseCase, useCaseIndex) => {
    if (!eachUseCase) {
      return null;
    }

    const fields = [
      "Title",
      "Primary Actor",
      "Goal in Context",
      "Scope",
      "Stakeholders and Interests",
      "Preconditions",
      "Minimal Guarantees",
      "Success Guarantees",
      "Trigger",
      "Main Success Scenario",
      "Extensions",
      "Technology",
      "Data Variations List",
    ];

    return (
      <Box key={`eachUseCase_${useCaseIndex}`}>
        {fields.map((field) => (
          <React.Fragment key={field}>
            {/* Side heading - only in non-edit mode */}
            {isEditing !== useCaseIndex && (
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "black",
                  marginBottom: 0.5,
                }}
              >
                {field}
              </Typography>
            )}

            {/* Edit mode */}
            {isEditing === useCaseIndex ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  name={field}
                  label={field}
                  value={editingValue[field]}
                  onChange={(e) => handleEdittedValue(e, useCaseIndex)}
                  sx={{ marginTop: 1, marginRight: 1, marginBottom: 2 }}
                  inputProps={{ style: { fontSize: "14px" } }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  "&:hover": { backgroundColor: "white" },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "14px", marginBottom: 2 }}
                >
                  {eachUseCase[field]
                    ?.toString()
                    .split("\n")
                    .map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                </Typography>
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  });

  const handleEdittedValue = (event) => {
    const updatedValue = {
      ...editingValue,
      [event.target.name]: event.target.value,
    };
    setEditingValue(updatedValue);
  };

  const handleMultipleTestConditions = async () => {
    let progressHandler;
    try {
      progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 8000 });
      progressHandler.start("Generating Test Conditions");
      if (selectedUseCase.every((eachUseCase) => !eachUseCase)) {
        setNoneUseCaseSelected(true);
        return;
      }
      setTestCaseLoaderState("Test Conditions");

      // Create an array of promises for concurrent calls
      const testConditionPromises = selectedUseCase.map(async (eachUseCase) => {
        if (eachUseCase && Object.keys(eachUseCase).length > 0) {
          const requestData = {
            functionalSpecDocument:
              functionalSpecDocument["Functional Requirements"],
            useCase: eachUseCase,
          };
          return generateTestConditions(requestData);
        }
        return null;
      });

      const responses = await Promise.all(testConditionPromises);

      // Process the responses and filter out nulls
      const testConditionsArray = responses
        .filter(response => response?.data)
        .map(response => response.data);
      await progressHandler.finishAndClose();
      navigate("/dashboard/generate-multi/testcases/output", {
        state: {
          ...location.state,
          testConditions: testConditionsArray,
          selectedMultipleUseCases: selectedUseCase,
        },
        replace: true,
      });
    } catch (error) {
      console.error("Error", error);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setTestCaseLoaderState(null);
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const createWordData = (useCasesData) => {
    const wordData = [];
    useCasesData.map((eachUseCase) => {
      if (!!eachUseCase) {
        wordData.push({
          heading: "Use Case",
          type: "subParagraphs",
          data: eachUseCase,
        });
        wordData.push({ type: "pageBreak" });
        return;
      }
    });
    return wordData;
  };

  const buttons = [
    {
      label: "Download",
      variant: "text",
      startIcon: <FileDownloadOutlinedIcon />,
      onClick: () => setOpenDownload(true),
    },
    {
      label: "Generate Test Conditions",
      variant: "contained",
      onClick: handleMultipleTestConditions,
      sx: { float: "right" },
      disabled:
        testCaseLoaderState === "Test Conditions" ||
        multipleUseCase.filter(Boolean).length === 0 ||
        selectedUseCase.filter(Boolean).length === 0
    },
  ];

  const [selectedStories, setSelectedStories] = useState(
    new Array(multipleUseCase.length).fill(true)
  );

  return (
    <Box sx={{ width: { xs: '100%', md: 966, lg: 1100 } }}>
      <CommonAccordion
        summary={`Use Cases (${activeUseCases.length})`}
        details={
          <Box>
            <Box sx={{ overflow: "hidden", width: "100%" }}>
              <MultiAccordian
                summaries={summaries}
                multiAccoridanDetails={useCaseComponents}
                multiAccoridanData={multipleUseCase}
                setMultiAccordianData={setMultipleUseCase}
                selectedAccordians={selectedUseCase}
                setSelectedAccordians={setSelectedUseCase}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                editingValue={editingValue}
                setEditingValue={setEditingValue}
                loaderCheck={!!testCaseLoaderState}
              />
            </Box>
          </Box>
        }
        buttons={buttons}
        type={"output"}
        fullScreenMode={true}
        defaultExpanded={defaultUseCaseExpanded}
      />
      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceName="Use Cases Report"
        wordData={createWordData(multipleUseCase)}
        serviceResponse={[
          {
            heading: "Use Cases",
            type: "subParagraphs",
            data: multipleUseCase
              .filter((useCase) => useCase != null)
              .map((useCase) => formatDataForPDF(useCase)),
          },
        ]}
        documents={["docx", "pdf"]}
      />
      <SelectionErrorPopUp
        open={noneUseCaseSelected}
        onClose={() => setNoneUseCaseSelected(false)}
      />
    </Box>
  );
};

export default MultipleUseCase;
